import { useAuth0 } from '@auth0/auth0-react';
import { AccountProvider } from '@my/platform/account';
import { DashboardLayout } from '@my/ui';
import { AppSidebar } from './AppSidebar';
import { PendingInvitationsPrompt } from './PendingInvitationsPrompt';
import { SyncLanguageWithOrg } from '@/localisation/SyncLanguageWithOrg';
import { useUiStore } from '@/stores/ui';

const ACCOUNT_ID_CLAIM = 'https://my/account_id';

function getAccountIdClaim(user: ReturnType<typeof useAuth0>['user']): string {
  if (!user) return '';
  const raw = (user as Record<string, unknown>)[ACCOUNT_ID_CLAIM];
  return typeof raw === 'string' ? raw : '';
}

/**
 * Safety-side wrapper around `<DashboardLayout>` that supplies the configured
 * `<AppSidebar>` and threads sidebar open/close state through the Zustand UI
 * store. Also wraps the authenticated tree with `<AccountProvider>` so every
 * screen can read `useAccount().currentAccountId`.
 */
export function AppShell() {
  const { user } = useAuth0();
  const isOpen = useUiStore((s) => s.isSidebarOpen);
  const toggle = useUiStore((s) => s.toggleSidebar);

  return (
    <AccountProvider defaultAccountId={getAccountIdClaim(user)}>
      {/* Side-effect bridge: keeps i18next.language in step with the loaded
          organisation's `languageCode`. Renders nothing. Mirrors the
          `AuthTokenBridge` pattern used for axios token wiring. */}
      <SyncLanguageWithOrg />
      {/* Post-login modal that surfaces pending account-membership
          invitations. Renders nothing when there are none. Lives at this
          level (rather than per-route) so the prompt fires regardless of
          which page the user lands on. */}
      <PendingInvitationsPrompt />
      <DashboardLayout sidebar={<AppSidebar />} isOpen={isOpen} onToggle={toggle} />
    </AccountProvider>
  );
}
